import { motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import Images from '../common/Images';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuItem } from '@/types/home';
import { showToastMessage } from '@/utils/helper';
import { useDispatch } from 'react-redux';
import { useLogoutMutation } from '@/store/api/authApi';
import { logout as clearAuth } from '@/store';
import { useIsAuthenticated } from '@/hooks';
import { Button } from '../ui/button';
const Sidebar = ({ setToggle }: { setToggle: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const naviate = useNavigate();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [logout, { isLoading: logoutLoading }] = useLogoutMutation();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    // trigger animation after mount
    requestAnimationFrame(() => setVisible(true));
  }, []);

  function handleClose() {
    setVisible(false);
    setTimeout(() => setToggle(false), 250); // wait for animation to finish
  }

  const handleLogout = useCallback(async () => {
    try {
      await logout().unwrap();
      dispatch(clearAuth());
      showToastMessage('success', 'You have been logged out successfully.');
      navigate('/login');
    } catch (err: any) {
      showToastMessage('error', err?.data?.message || 'Logout failed. Please try again.');
    }
  }, [logout, dispatch, navigate]);
  const handleClick = (item: MenuItem) => {
    if (item.path) {
      naviate(item.path);
      handleClose();
      return;
    }
    if (item.action === 'logout') {
      handleLogout();
      return;
    }
  };
  const menuItems: MenuItem[] = [
    {
      id: 1,
      label: 'Home',
      img: '/sidebar/8.webp',
      path: '/',
    },
    {
      id: 2,
      label: 'Profile',
      img: '/sidebar/2.webp',
      path: '/profile',
    },
    {
      id: 3,
      label: 'Sports',
      img: '/sidebar/4.svg',
      path: '/live',
    },
    {
      id: 4,
      label: 'Quiz',
      img: '/sidebar/1.webp',
      path: '/quiz',
    },
    {
      id: 5,
      label: 'Leaderboard',
      img: '/sidebar/5.webp',
      path: '/campaign-leaderboard',
    },
    {
      id: 6,
      label: 'Campaigen',
      img: '/sidebar/9.webp',
      path: '/acitve-campaign',
    },

    {
      id: 7,
      label: 'Quiz History',
      img: '/sidebar/7.webp',
      path: '/game-history',
    },
    {
      id: 8,
      label: 'Quiz Results',
      img: '/sidebar/10.webp',
      path: '/quiz-results',
    },
    {
      id: 9,
      label: 'Transactions',
      img: '/sidebar/11.svg',
      path: '/transections',
    },

    {
      id: 10,
      label: 'Referral',
      img: '/sidebar/3.webp',
      path: '/referral',
    },
    ...(isAuthenticated
      ? [
          {
            id: 11,
            label: 'Logout',
            img: '/sidebar/6.webp',
            action: 'logout',
          },
        ]
      : []),
  ];
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      x: -20,
    },
    show: {
      opacity: 1,
      x: 0,
    },
  };
  return (
    <>
      {/* BACKDROP */}
      <div
        className={`absolute inset-0 z-40 bg-black/70 backdrop-blur-sm
                    transition-opacity duration-250
                    ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />
      {/* SIDEBAR */}
      <aside
        className={`absolute left-0 top-0 z-50 h-full w-[75%] max-w-[300px] overflow-y-auto border-r border-white/10
                    bg-black/10 backdrop-blur-xl px-4 py-5
                    transition-transform duration-250 ease-out
                    ${visible ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* TOP SECTION */}
        <div className="mb-4 flex items-center justify-between">
          {/* CLOSE BUTTON */}
          <button onClick={() => setToggle(false)} className="h-10 w-10 ">
            <Images src="/home/toggle2.svg" alt="ball" />
          </button>
        </div>
        {!isAuthenticated ? (
          <div className="flex flex-col gap-4">
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
              <Button
                className="w-full bg-btn-gradient2 shadow-[0_0_25px_rgba(255,88,20,0.35)]"
                onClick={() => navigate('/login')}
              >
                Login
              </Button>
            </motion.div>

            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
              <Button
                className="w-full border border-orange-500/30 bg-transparent"
                onClick={() => navigate('/signup')}
              >
                Sign Up
              </Button>
            </motion.div>
          </div>
        ) : (
          <motion.div
            className="flex flex-col gap-4"
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <motion.button
                  key={item.id}
                  variants={itemVariants}
                  onClick={() => handleClick(item)}
                  className={`flex h-10 items-center gap-4 rounded-full border px-5 text-left transition-all duration-200 ${
                    isActive
                      ? 'border-orange-400 bg-btn-gradient2 text-white shadow-lg shadow-orange-500/20'
                      : 'border-white/20 bg-black text-white hover:border-white/40 hover:bg-white/5'
                  }`}
                >
                  <div className="bg-primary-tabg1 p-1 rounded-full">
                    <Images src={item.img || ''} className="w-6" alt={item.label} />
                  </div>

                  <span className=" text-sm font-medium tracking-wide">{item.label}</span>
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
